import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ChatProvider } from "./utils/chatContext";
import { ChatPage } from "./pages/ChatPage";
import { ReviewPage } from "./pages/ReviewPage";
import { LoginPage } from "./pages/LoginPage";
import { AdminPage } from "./pages/AdminPage";
import { IS_TAURI } from "./utils/backend";
import "./index.css";

type Role = "admin" | "embed";

// Embedded deployments (a host page iframing `/?embed_token=...`) authenticate
// with a per-token secret instead of the username/password form. The token is
// stashed in sessionStorage (survives reloads, dies with the tab) and stripped
// from the URL so it doesn't leak into copy/pasted links.
const EMBED_TOKEN_KEY = "embed_token";

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage can be blocked in third-party iframes.
  }
}

function takeEmbedToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("embed_token");
  if (token) {
    storageSet(EMBED_TOKEN_KEY, token);
    params.delete("embed_token");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      qs ? `?${qs}` : window.location.pathname,
    );
    return token;
  }
  return storageGet(EMBED_TOKEN_KEY);
}

async function checkAuth(): Promise<Role | null> {
  // 1. Existing session cookie from a previous login.
  try {
    const res = await fetch("/api/auth-check");
    if (res.ok) {
      const data: { role?: Role } = await res.json().catch(() => ({}));
      return data.role === "embed" ? "embed" : "admin";
    }
  } catch {
    /* fall through to the embed token exchange */
  }

  // 2. Embed token: exchange it for a session. `from` is the host page's
  // URL (the iframe's referrer) — origin-pinned tokens require it to match.
  const token = takeEmbedToken();
  if (!token) return null;

  try {
    const res = await fetch("/api/embed-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, from: document.referrer || null }),
    });
    if (res.ok) return "embed";
  } catch {
    /* fall through */
  }

  storageSet(EMBED_TOKEN_KEY, null);
  return null;
}

function App() {
  // Tauri: always authed, full operator access.
  const [role, setRole] = useState<Role | null>(IS_TAURI ? "admin" : null);
  const [checking, setChecking] = useState(!IS_TAURI);
  const [view, setView] = useState<"chat" | "review">("chat");
  const isAdminRoute = window.location.pathname
    .replace(/\/+$/, "")
    .startsWith("/admin");

  useEffect(() => {
    if (IS_TAURI) return;

    checkAuth().then((r) => {
      setRole(r);
      setChecking(false);
    });
  }, []);

  if (checking) return null;

  // /admin manages embed tokens and requires a password login — an embed
  // session (or no session) gets the login form instead.
  if (isAdminRoute) {
    return role === "admin" ? (
      <AdminPage />
    ) : (
      <LoginPage onLogin={() => setRole("admin")} />
    );
  }

  if (!role) {
    return <LoginPage onLogin={() => setRole("admin")} />;
  }

  const isAdmin = role === "admin";

  if (isAdmin && view === "review") {
    return <ReviewPage onBack={() => setView("chat")} />;
  }

  return (
    <ChatProvider isAdmin={isAdmin}>
      <ChatPage
        isAdmin={isAdmin}
        onOpenReview={isAdmin ? () => setView("review") : undefined}
      />
    </ChatProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
